<template>
    <div>
        <slot name="controls"
              :loop="loop"
              :slide-prev="() => swiper?.slidePrev(speed)"
              :slide-next="() => swiper?.slideNext(speed)"
              :is-beginning="swiper?.isBeginning || false"
              :is-end="swiper?.isEnd || false" />
        <div ref="root" class="relative z-10 mx-auto block list-none p-0" :class="containerClasses">
            <div class="slider-wrapper relative z-10 box-content h-full transition-transform"
                 :class="[wrapperClasses, { 'flex': !editMode }]">
                <slot />
            </div>
        </div>
        <slot name="pagination"
              :index="index"
              :total="total"
              :loop="loop"
              :slide-data="currentSlideData"
              :slide-to="slideTo"
              :slide-prev="() => swiper?.slidePrev(speed)"
              :slide-next="() => swiper?.slideNext(speed)"
              :is-beginning="swiper?.isBeginning || false"
              :is-end="swiper?.isEnd || false" />
    </div>
</template>

<script lang="ts" setup>
import Swiper from 'swiper';
import { Autoplay } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import {
    onMounted,
    ref,
    onUnmounted,
    watch,
    withDefaults,
    defineProps,
    defineEmits,
    defineExpose
} from 'vue';
import type { Ref } from 'vue';
import { getTwConfig } from '@/utils';
import events from '@/events';

interface SliderProps {
    loop?: boolean;
    nested?: boolean;
    gap?: number | { [breakpoint: string]: number };
    containerClasses?: string;
    slideClasses?: string;
    wrapperClasses?: string;
    start?: number;
    speed?: number;
    centered?: boolean | object;
    autoplay?: boolean;
    autoplayDelay?: number;
    editMode?: boolean;
    slidesPerView?: number | object | string;
    disabled?: boolean;
    globalEvent?: string;
}

const props = withDefaults(defineProps<SliderProps>(), {
    loop: false,
    nested: false,
    gap: 0,
    containerClasses: '',
    slideClasses: '',
    wrapperClasses: 'w-full',
    start: 0,
    speed: 300,
    centered: false,
    autoplay: false,
    autoplayDelay: 5000,
    editMode: false,
    slidesPerView: 'auto',
    disabled: false,
    globalEvent: ''
});
const root: Ref<HTMLElement | null> = ref(null);
const swiper: Ref<Swiper | null> = ref(null);
const twConfig = getTwConfig();
const emit = defineEmits(['change']);
const total = ref(0);
const index = ref(0);
const currentSlideData = ref({});
const options = {
    watchOverflow: true,
    wrapperClass: 'slider-wrapper',
    slideClass: 'slide',
    observer: true,
    loopPreventsSliding: false,
    nested: props.nested,
    loop: props.loop || props.autoplay, // always loop in autoplay
    speed: props.speed,
    autoplay: props.autoplay ? { delay: props.autoplayDelay, disableOnInteraction: false } : false,
    touchReleaseOnEdges: true,
    initialSlide: props.start,
    observeParents: true,
    slideActiveClass: 'slide-active',
    slideNextClass: 'slide-next',
    slidePrevClass: 'slide-prev',
    watchSlidesProgress: true,
    slideToClickedSlide: true,
    // workarounds for swipers loop bugs
    on: {
        init: (e: Swiper) => {
            if (props.loop) {
                // @ts-ignore (apparently loopFix doesn't exist,  but it does, I swear!)
                e.loopFix();
            }
            total.value = e.slides.length;
            setTimeout(getSlideData, 150);
        },
        // the slideChange event is fired twice on loop (swiper bug)
        // instead realIndexChange has the expected behaviour
        realIndexChange: (e: Swiper) => {
            index.value = e.realIndex;
            emit('change', index.value);
            getSlideData();
            if (props.globalEvent.length) {
                events.$emit(props.globalEvent, currentSlideData.value);
            }
        },
        slideNextTransitionEnd: (e: Swiper) => {
            if (props.loop) {
                // @ts-ignore
                e.loopFix({ direction: 'next', setTranslate: true });
            }
        },
        slidePrevTransitionEnd: (e: Swiper) => {
            if (props.loop) {
                // @ts-ignore
                e.loopFix({ direction: 'prev', setTranslate: true });
            }
        }
    }
} as SwiperOptions;

defineExpose({ swiper });

onMounted(() => {
    if (props.editMode) {
        return;
    }
    setBreakpointOption(props.gap, 'spaceBetween');
    setBreakpointOption(props.slidesPerView, 'slidesPerView');
    setBreakpointOption(props.centered, 'centeredSlides');
    if (props.autoplay) {
        Swiper.use([Autoplay]);
    }
    init();
});

onUnmounted(() => {
    destroy();
});

watch(
    () => props.disabled,
    () => {
        props.disabled ? destroy() : init();
    }
);

function init() {
    if (root.value) {
        swiper.value = new Swiper(root.value, options);
    }
}

function destroy() {
    swiper.value?.destroy();
}

function slideTo(index: number) {
    if (options.loop) {
        swiper.value?.slideToLoop(index, props.speed)
    } else {
        swiper.value?.slideTo(index, props.speed)
    }
}

function getSlideData() {
    if (swiper.value && swiper.value.slides && swiper.value.slides.length > index.value) {
        // order of swiper.slides is not fixed, so we can't just take the current index.
        // instead a fixed index is expected in the dataset of the slide element
        const slide = swiper.value.slides.find(x => (x.dataset && x.dataset.index) ? parseInt(x.dataset.index) === swiper.value.realIndex : null);
        if (slide && slide.dataset && slide.dataset.slide) {
            currentSlideData.value = slide.dataset.slide.split(';').map(x => x.trim().split(':')).reduce((r, x) => {
                // eslint-disable-next-line no-eval
                r[x[0]] = eval(x[1].trim());
                return r;
            }, {});
            return;
        }
    }
    currentSlideData.value = {};
}

function setBreakpointOption(value: number | string | boolean | object, name: keyof SwiperOptions) {
    const breakpoints = options.breakpoints || {};
    if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
        // @ts-ignore
        options[name] = value;
        return;
    }
    Object.entries(value).forEach(x => {
        const bp = breakpointSize(x[0]);
        if (bp > 0) {
            if (!Object.prototype.hasOwnProperty.call(breakpoints, bp)) {
                breakpoints[bp] = {};
            }
            // @ts-ignore
            breakpoints[bp][name] = x[1];
        } else {
            // @ts-ignore
            options[name] = x[1];
        }
    });
    if (!options.breakpoints) {
        options.breakpoints = breakpoints;
    }
}

function breakpointSize(name: string): number {
    if (Object.prototype.hasOwnProperty.call(twConfig.theme.screens, name)) {
        // @ts-ignore
        return parseInt(twConfig.theme?.screens[name]);
    }
    return -1;
}
</script>
